/**
 * TabRouterContext — the router a tab's page sees.
 *
 * One provider per tab, **always in the same position**, whose values depend on
 * whether that tab owns the browser URL.
 *
 * The position is the part that bites. An earlier version chose between two
 * different components — a passthrough for the tab that owns the URL, the
 * synthesised pane provider for the rest — and that was invisible while one tab
 * rendered at a time. Mount two and switch between them and the outgoing tab's
 * wrapper changes type, so React discards the subtree and rebuilds it: the tab
 * comes back as a fresh page, which is the exact failure the whole design
 * exists to remove. The three providers below are now unconditional and only
 * their *values* change.
 *
 * **The condition is "owns the URL", not "is active".** Both panes have an
 * active tab, but only the primary group owns `window.location`. Selecting
 * passthrough on per-group activeness would hand the secondary pane's active
 * tab the *primary* pane's URL, and its navigations would drive the primary
 * route.
 *
 * - **Owns it** — the real `NavigationContext` and `LocationContext` pass
 *   through, so `useNavigate`, `useLocation`, back and forward behave exactly
 *   as they did under `<Routes>`.
 * - **Does not** — both are synthesised from the tab's stored location, so a
 *   `navigate()` inside the tab moves that tab rather than the browser.
 *
 * The route context is synthesised either way: `<Routes>` used to populate it
 * and no page renders inside one any more, so without it `useParams()` returns
 * `{}` in every page that has a dynamic segment.
 */
import React, { useContext } from 'react';

import {
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
} from 'react-router-dom';

import { usePaneRouterValues } from '../paneRouterValues';

import type { WorkspaceTab } from '../types';

interface Props {
  tab: WorkspaceTab;
  /**
   * What this tab is rendering: the live URL for the tab that owns it, else the
   * tab's stored path. Not `tab.path` — for the owning tab that is the mirror,
   * and it lags the URL by an effect, which would resolve a relative link
   * against where the tab was rather than where it is.
   */
  path: string;
  /** The params the table's match captured for `path`. */
  params: Record<string, string>;
  /** True for exactly one tab in the app: the primary group's active one. */
  ownsBrowserUrl: boolean;
  children: React.ReactNode;
}

/** Wrap a tab's page in the router context that tab should see. */
export const TabRouterContext: React.FC<Props> = ({
  tab,
  path,
  params,
  ownsBrowserUrl,
  children,
}) => {
  const synthesised = usePaneRouterValues({
    tabId: tab.id,
    pathname: path,
    params,
    state: tab.state,
    locationKey: tab.locationKey,
  });
  // The contexts this tab is rendered under — the real router's, for the tab
  // that owns the URL. Read unconditionally: hooks cannot be skipped, and the
  // values are cheap.
  const realNavigation = useContext(UNSAFE_NavigationContext);
  const realLocation = useContext(UNSAFE_LocationContext);

  return (
    <UNSAFE_NavigationContext.Provider
      value={ownsBrowserUrl ? realNavigation : synthesised.navigation}
    >
      <UNSAFE_LocationContext.Provider
        value={ownsBrowserUrl ? realLocation : synthesised.location}
      >
        <UNSAFE_RouteContext.Provider value={synthesised.route}>
          {children}
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_LocationContext.Provider>
    </UNSAFE_NavigationContext.Provider>
  );
};
